/**
 * "Reader app" external checkout — Apple App Store compliance.
 *
 * Apple treats an eSIM data plan as a digital service; if we sold it via a
 * WebView inside the app, App Review would reject us for bypassing In-App
 * Purchase. The compliant pattern used by Airalo, Holafly and similar apps
 * is: the app browses/manages, and the "Buy" button opens the OS-level
 * browser at the marketing website's checkout URL. Revolut Checkout keeps
 * working, no IAP fee.
 *
 * On the web build this collapses to a normal same-origin navigation so
 * nothing changes for browser users.
 */

import { Browser } from '@capacitor/browser';
import { isNative, WEB_URL } from './platform';

/**
 * @param {string} internalPath  path like `/checkout?planId=...`
 */
export async function openExternalCheckout(internalPath) {
  const safePath = internalPath.startsWith('/') ? internalPath : `/${internalPath}`;
  if (!isNative()) {
    // Preserve existing web behaviour — same-origin push
    window.location.assign(safePath);
    return;
  }
  const url = `${WEB_URL}${safePath}`;
  // Opens in Safari View Controller (iOS) / Chrome Custom Tabs (Android).
  // This shares the user's browser cookies + is App Review-safe.
  await Browser.open({
    url,
    presentationStyle: 'popover',
    windowName: '_self',
  });
}

/**
 * Convenience wrapper used from the plan card "Buy Now" button.
 */
export async function buyPlanExternally({ planId, countryId, currency }) {
  const params = new URLSearchParams();
  if (planId) params.set('planId', planId);
  if (countryId) params.set('countryId', countryId);
  if (currency) params.set('currency', currency);
  const qs = params.toString();
  await openExternalCheckout(`/checkout${qs ? `?${qs}` : ''}`);
}
